import React from 'react';
import RetryButton from './RetryButton';
import './ErrorState.css';

interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  retryLoading?: boolean;
  showRetry?: boolean;
  icon?: 'error' | 'network' | 'offline' | 'warning';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  retryLabel = 'Try Again',
  retryLoading = false,
  showRetry = true,
  icon = 'error',
  size = 'medium',
  className = '',
}) => {
  const getIcon = () => {
    switch (icon) {
      case 'network':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a11 11 0 0 1 0 17"/>
            <path d="M6.5 3.5a11 11 0 0 0 0 17"/>
            <path d="M12 20v-8"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
        );
      case 'offline':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a11 11 0 0 1 0 17"/>
            <path d="M6.5 3.5a11 11 0 0 0 0 17"/>
            <line x1="2" y1="2" x2="22" y2="22"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      case 'error':
      default:
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
    }
  };

  return (
    <div className={`error-state ${size} ${className}`}>
      <div className="error-state-content">
        <div className={`error-state-icon ${icon}`}>
          {getIcon()}
        </div>
        
        <div className="error-state-text">
          <h3 className="error-state-title">{title}</h3>
          <p className="error-state-message">{message}</p>
        </div>

        {showRetry && onRetry && (
          <div className="error-state-actions">
            <RetryButton
              onRetry={onRetry}
              loading={retryLoading}
              variant="primary"
              size={size === 'large' ? 'medium' : 'small'}
            >
              {retryLabel}
            </RetryButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorState;